import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ToofiIconRail } from "@/components/ui/reader-ai/toofi-icon-rail";
import { ToofiMobileNavDrawer } from "@/components/ui/reader-ai/toofi-mobile-nav-drawer";
import { ToofiSidebar } from "@/components/ui/reader-ai/toofi-sidebar";
import { ToofiMark } from "@/components/ui/toofi-mark";
import { ToofiFloatingIconButton, ToofiShellSurface } from "@/components/ui/toofi-shell-chrome";
import { ToofiThreadShell } from "@/components/ui/toofi-thread-shell";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useStudentToofi } from "@/hooks/use-student-toofi";
import { useToofiArtifacts } from "@/hooks/useToofiArtifacts";
import { useToofiResume } from "@/hooks/useToofiResume";
import { buildStudentToofiSeedIntent } from "@/lib/student-toofi-open-intent";
import { captureToofiAnalytics } from "@/lib/toofi-analytics";
import { cn } from "@/lib/utils";
import type { ToofiShellSidebarView, ToofiWorkspaceView } from "@/components/ui/reader-ai/types";
import { ArrowLeft, Expand, Menu, Minimize2, X } from "lucide-react";

export function StudentToofiShell() {
  const {
    available,
    open,
    activeThreadId,
    threads,
    messages,
    promptDraft,
    surfaceContext,
    promptWarning,
    currentProductMode,
    readerSurfaceOpen,
    openToofi,
    closeToofi,
    backHome,
    startNewChat,
    startTemporaryChat,
    switchThread,
    renameThread,
    deleteThread,
    pinThread,
    unpinThread,
    archiveThread,
    restoreThread,
    startSimilarChat,
    setThreadMemoryMode,
    startChatWithPrompt,
    submitPrompt,
    setCurrentProductMode,
    setPromptDraft,
    editMessage,
    deleteMessage,
    submitMcq,
    startEssay,
    changeEssayDraft,
    submitEssay,
    submitGuidedLessonAnswer,
    continueGuidedLesson,
    skipGuidedLessonStep,
    savePlan,
    refinePlan,
  } = useStudentToofi();
  const navigate = useNavigate();
  const {
    artifacts,
    isLoading: artifactItemsLoading,
    openArtifact,
    deleteArtifact,
    setArtifactPinned,
  } = useToofiArtifacts();
  const { resumeTarget } = useToofiResume();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const [phoneViewportHeight, setPhoneViewportHeight] = useState<number | null>(null);
  const [phoneViewportTop, setPhoneViewportTop] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [phoneNavOpen, setPhoneNavOpen] = useState(false);
  const [sidebarView, setSidebarView] = useState<ToofiShellSidebarView>("chats");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showArchivedThreads, setShowArchivedThreads] = useState(false);
  const activeThread = threads.find((thread) => thread.id === activeThreadId) ?? null;

  const handleOpenArtifact = async (artifactId: string) => {
    const artifact = await openArtifact(artifactId);
    if (!artifact) return;
    if (artifact.scope === "workspace") {
      setSidebarView("chats");
      setPhoneNavOpen(false);
      openToofi(
        buildStudentToofiSeedIntent({
          threadId: artifact.threadId,
          linkedResourceId: artifact.resourceId,
          linkedPlanId: artifact.linkedPlanId,
          productMode: artifact.kind.startsWith("plan_") || artifact.kind === "daily_plan" ? "plan" : "learn",
          entryPointId: "artifact_reopen",
        }),
      );
      return;
    }
    if (artifact.sourcePagePath) {
      navigate(artifact.sourcePagePath);
      return;
    }
    if (artifact.resourceId) {
      navigate(`/resource/${artifact.resourceId}?reader=1`);
    }
  };

  useEffect(() => {
    if (!isPhone || !open) {
      setPhoneViewportHeight(null);
      setPhoneViewportTop(0);
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) return;

    const updateViewport = () => {
      setPhoneViewportHeight(Math.round(viewport.height));
      setPhoneViewportTop(Math.max(0, Math.round(viewport.offsetTop)));
    };

    updateViewport();
    viewport.addEventListener("resize", updateViewport);
    viewport.addEventListener("scroll", updateViewport);

    return () => {
      viewport.removeEventListener("resize", updateViewport);
      viewport.removeEventListener("scroll", updateViewport);
    };
  }, [isPhone, open]);

  useEffect(() => {
    if (!open) {
      setPhoneNavOpen(false);
      setSidebarView("chats");
      setExpanded(false);
      setSidebarCollapsed(false);
      setShowArchivedThreads(false);
    }
  }, [open]);

  if (!available || readerSurfaceOpen) {
    return null;
  }

  const isArtifactView = sidebarView === "artifacts";
  const hideFloatingLauncher = surfaceContext.surface === "resource_detail";
  const dockWidthClass = isTablet
    ? "w-[min(72rem,calc(100vw-1.5rem))]"
    : "w-[min(76rem,calc(100vw-1.75rem))] xl:w-[min(82rem,calc(100vw-2.25rem))]";
  const dockInsetClass = isTablet
    ? "bottom-4 right-4 top-4"
    : "bottom-5 right-5 top-5 xl:bottom-6 xl:right-6 xl:top-6";
  const mainView: ToofiWorkspaceView = isArtifactView ? "artifacts" : activeThreadId ? "thread" : "home";
  const headerTitle = activeThread?.title?.trim() || (isArtifactView ? "Saved outputs" : "Toofi");

  const handleResumeTarget = () => {
    if (!resumeTarget) return;
    if (resumeTarget.kind === "artifact" && resumeTarget.artifactId) {
      void handleOpenArtifact(resumeTarget.artifactId);
      return;
    }
    if (!resumeTarget.threadId) return;
    setSidebarView("chats");
    captureToofiAnalytics("toofi_resume_opened", {
      kind: resumeTarget.kind,
      threadId: resumeTarget.threadId,
      resourceId: resumeTarget.resourceId ?? null,
      linkedPlanId: resumeTarget.linkedPlanId ?? null,
      artifactId: resumeTarget.artifactId ?? null,
      surface: "workspace_shell",
    });
    openToofi(
      buildStudentToofiSeedIntent({
        threadId: resumeTarget.threadId,
        linkedResourceId: resumeTarget.resourceId ?? null,
        linkedPlanId: resumeTarget.linkedPlanId ?? null,
        productMode:
          resumeTarget.kind === "guided_lesson"
            ? "learn"
            : resumeTarget.kind === "review"
              ? "review"
              : resumeTarget.kind === "plan"
                ? "plan"
                : resumeTarget.kind === "essay"
                  ? "create"
                  : "learn",
        entryPointId: "workspace_resume_toofi",
      }),
    );
  };

  const handleShellBack = () => {
    if (isArtifactView) {
      setSidebarView("chats");
      setPhoneNavOpen(false);
      return;
    }
    if (activeThreadId) {
      backHome();
      return;
    }
    closeToofi();
  };

  const handleSwitchThread = (threadId: string) => {
    setSidebarView("chats");
    setPhoneNavOpen(false);
    switchThread(threadId);
  };

  const handleNewChat = () => {
    setSidebarView("chats");
    setPhoneNavOpen(false);
    startNewChat();
  };

  const handleStartTemporaryChat = () => {
    setSidebarView("chats");
    setPhoneNavOpen(false);
    startTemporaryChat();
  };

  const handleShowChats = () => {
    setSidebarView("chats");
    if (activeThreadId) {
      backHome();
    }
  };

  const handleShowArtifacts = () => {
    setSidebarView("artifacts");
  };

  const handleToggleArchived = () => {
    setSidebarView("chats");
    setShowArchivedThreads((current) => !current);
  };

  const handleToggleExpanded = () => {
    setExpanded((current) => {
      const next = !current;
      if (next) {
        captureToofiAnalytics("toofi_shell_fullscreen_opened", { source: "workspace_shell" });
      }
      return next;
    });
  };

  const showBackState = isArtifactView || Boolean(activeThreadId);

  return (
    <>
      {!hideFloatingLauncher ? (
        <Button
          type="button"
          size="icon"
          aria-label="Open Toofi"
          title="Toofi"
          className={cn(
            "fixed z-[95] rounded-full border border-[var(--athr-border-soft)] bg-[linear-gradient(180deg,rgba(255,252,245,0.96),rgba(241,245,249,0.9))] text-slate-800 shadow-[0_20px_44px_rgba(15,23,42,0.18)] backdrop-blur-2xl transition-all duration-200 hover:-translate-y-0.5 active:scale-95",
            isPhone
              ? "bottom-[max(1.35rem,calc(env(safe-area-inset-bottom)+0.75rem))] right-[max(1rem,calc(env(safe-area-inset-right)+0.7rem))] h-12 w-12"
              : isTablet
                ? "bottom-5 right-5 h-14 w-14"
                : "bottom-6 right-6 h-16 w-16",
            open && "pointer-events-none translate-y-3 opacity-0",
          )}
          onClick={() => openToofi()}
        >
          <ToofiMark transparent className={cn("h-10 w-10 [&_svg]:!h-full [&_svg]:!w-full", isPhone && "h-8 w-8")} />
        </Button>
      ) : null}

      {isPhone ? (
        <div
          className={cn(
            "fixed inset-x-0 top-0 z-[96] flex flex-col overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.985),rgba(248,250,252,0.97))] px-2 pb-[max(0.65rem,env(safe-area-inset-bottom))] pt-[max(0.45rem,env(safe-area-inset-top))] backdrop-blur-2xl transition-all duration-200",
            open ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0",
          )}
          style={{
            top: phoneViewportTop || 0,
            height: phoneViewportHeight ? `${phoneViewportHeight}px` : undefined,
          }}
        >
          <div className="mb-1 flex items-center justify-between gap-2 border-b border-white/70 px-1 pb-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={showBackState ? "Go back" : "Close Toofi"}
              className="h-9 w-9 rounded-full border border-transparent text-slate-500 hover:bg-slate-100/80 hover:text-slate-900"
              onClick={handleShellBack}
            >
              {showBackState ? <ArrowLeft className="h-5 w-5" /> : <X className="h-4 w-4" />}
            </Button>
            <div className="min-w-0 flex-1 px-2 text-center">
              <p className="truncate text-[12px] font-semibold tracking-[-0.01em] text-slate-900">{headerTitle}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Open chats and artifacts"
              className="h-9 w-9 rounded-full border border-transparent text-slate-500 hover:bg-slate-100/80 hover:text-slate-900"
              onClick={() => {
                captureToofiAnalytics("toofi_sidebar_opened", { source: "mobile_header" });
                setPhoneNavOpen(true);
              }}
            >
              <Menu className="h-4.5 w-4.5" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            <ToofiThreadShell
              surfaceKind="workspace"
              userName={undefined}
              messages={messages}
              threads={threads}
              activeThreadId={activeThreadId}
              activeSourceRef={null}
              promptDraft={promptDraft}
              promptWarning={promptWarning}
              currentProductMode={currentProductMode}
              isPhone
              showSidebar={false}
              mainView={mainView}
              sidebarView={sidebarView}
              onSidebarViewChange={setSidebarView}
              onBackHome={backHome}
              onProductModeChange={setCurrentProductMode}
              onNewChat={handleNewChat}
              onStartTemporaryChat={handleStartTemporaryChat}
              onStartChatWithPrompt={(prompt, selection) =>
                void startChatWithPrompt(
                  prompt,
                  selection?.productMode || selection?.productIntent
                    ? {
                        productMode: selection.productMode ?? null,
                        productIntent: selection.productIntent ?? null,
                        modeSelectionSource: "manual",
                      }
                    : null,
                )
              }
              onSwitchThread={handleSwitchThread}
              onRenameThread={renameThread}
              onDeleteThread={deleteThread}
              onPinThread={pinThread}
              onUnpinThread={unpinThread}
              onArchiveThread={archiveThread}
              onRestoreThread={restoreThread}
              onStartSimilarChat={startSimilarChat}
              onSetThreadMemoryMode={setThreadMemoryMode}
              activeThreadMemoryMode={activeThread?.memoryMode ?? "default"}
              artifactItems={artifacts}
              artifactItemsLoading={artifactItemsLoading}
              onOpenArtifact={(artifactId) => void handleOpenArtifact(artifactId)}
              onDeleteArtifact={(artifactId) => void deleteArtifact(artifactId)}
              onSetArtifactPinned={(artifactId, pinned) => void setArtifactPinned(artifactId, pinned)}
              onPromptDraftChange={setPromptDraft}
              onPromptSubmit={(prompt, selection) =>
                void submitPrompt(
                  prompt,
                  selection?.productMode || selection?.productIntent
                    ? {
                        productMode: selection.productMode ?? null,
                        productIntent: selection.productIntent ?? null,
                        modeSelectionSource: "manual",
                      }
                    : null,
                )
              }
              onEditMessage={editMessage}
              onDeleteMessage={deleteMessage}
              onMcqSubmit={submitMcq}
              onEssayStart={startEssay}
              onEssayDraftChange={changeEssayDraft}
              onEssaySubmit={(blockId, prompt, draft) => void submitEssay(blockId, prompt, draft)}
              onGuidedLessonSubmit={(input) => void submitGuidedLessonAnswer(input)}
              onGuidedLessonContinue={(input) => void continueGuidedLesson(input)}
              onGuidedLessonSkip={(input) => void skipGuidedLessonStep(input)}
              onPlanSave={(messageId, blockId) => void savePlan(messageId, blockId)}
              onPlanRefine={refinePlan}
              onOpenTransparency={() => undefined}
              homeSubtitle={surfaceContext.subtitle}
              homeResumeTarget={resumeTarget}
              onResumeTarget={handleResumeTarget}
            />
          </div>
          <ToofiMobileNavDrawer
            open={phoneNavOpen}
            onOpenChange={setPhoneNavOpen}
            currentView={sidebarView}
            threads={threads}
            activeThreadId={activeThreadId}
            artifactCount={artifacts.length}
            onViewChange={setSidebarView}
            onNewChat={handleNewChat}
            onStartTemporaryChat={handleStartTemporaryChat}
            onSelectThread={handleSwitchThread}
            onRenameThread={renameThread}
            onDeleteThread={deleteThread}
            onPinThread={pinThread}
            onUnpinThread={unpinThread}
            onArchiveThread={archiveThread}
            onRestoreThread={restoreThread}
            onStartSimilarChat={startSimilarChat}
            onSetThreadMemoryMode={setThreadMemoryMode}
          />
        </div>
      ) : (
        <ToofiShellSurface
          as="section"
          fullScreen={expanded}
          className={cn(
            "fixed z-[96]",
            open ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0",
          )}
          dockedClassName={cn(dockInsetClass, dockWidthClass)}
        >
          <div
            className={cn(
              "absolute right-3 top-3 z-20 flex items-center gap-2 lg:right-4 lg:top-4",
              expanded && "right-4 top-4 lg:right-5 lg:top-5",
            )}
          >
            <ToofiFloatingIconButton
              label={expanded ? "Exit full screen" : "Open full screen"}
              title={expanded ? "Exit full screen" : "Open full screen"}
              icon={expanded ? <Minimize2 className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
              onClick={handleToggleExpanded}
            />
            <ToofiFloatingIconButton
              label="Close Toofi"
              icon={<X className="h-4 w-4" />}
              onClick={closeToofi}
            />
          </div>
          <div
            className={cn(
              "flex h-full min-w-0 flex-col overflow-hidden",
              expanded
                ? "px-4 pb-4 pt-4"
                : isTablet
                  ? "px-3.5 pb-3.5 pt-3.5"
                  : "px-[clamp(1rem,1.35vw,1.4rem)] pb-[clamp(1rem,1.35vw,1.4rem)] pt-[clamp(1rem,1.35vw,1.4rem)]",
            )}
          >
            <div className="min-h-0 flex flex-1 gap-3 overflow-hidden">
              <ToofiIconRail
                currentView={sidebarView}
                sidebarCollapsed={sidebarCollapsed}
                showArchived={showArchivedThreads}
                className={cn("w-14 shrink-0", expanded ? "rounded-[1.5rem]" : "rounded-[1.7rem]")}
                onNewChat={handleNewChat}
                onStartTemporaryChat={handleStartTemporaryChat}
                onShowChats={handleShowChats}
                onShowArtifacts={handleShowArtifacts}
                onToggleArchived={handleToggleArchived}
                onToggleSidebar={() => setSidebarCollapsed((current) => !current)}
              />
              <div
                className={cn(
                  "min-h-0 shrink-0 overflow-hidden transition-[width,opacity,transform] duration-300 ease-out",
                  sidebarCollapsed
                    ? "pointer-events-none w-0 -translate-x-3 opacity-0"
                    : isTablet
                      ? "w-64 opacity-100"
                      : "w-[18.25rem] opacity-100",
                )}
              >
                <ToofiSidebar
                  className="h-full"
                  threads={threads}
                  activeThreadId={activeThreadId}
                  artifactCount={artifacts.length}
                  currentView={sidebarView}
                  showArchived={showArchivedThreads}
                  showHeaderActions={false}
                  showViewSwitch={false}
                  onViewChange={setSidebarView}
                  onNewChat={handleNewChat}
                  onStartTemporaryChat={handleStartTemporaryChat}
                  onToggleArchived={handleToggleArchived}
                  onSelectThread={handleSwitchThread}
                  onRenameThread={renameThread}
                  onDeleteThread={deleteThread}
                  onPinThread={pinThread}
                  onUnpinThread={unpinThread}
                  onArchiveThread={archiveThread}
                  onRestoreThread={restoreThread}
                  onStartSimilarChat={startSimilarChat}
                  onSetThreadMemoryMode={setThreadMemoryMode}
                />
              </div>
              <div
                className={cn(
                  "min-w-0 flex-1 overflow-hidden",
                  expanded
                    ? "rounded-none border-0 bg-transparent shadow-none"
                    : "rounded-[2rem] border border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.86),rgba(248,250,252,0.74))] shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_20px_44px_rgba(148,163,184,0.08)] backdrop-blur-2xl",
                )}
              >
                <ToofiThreadShell
                  surfaceKind="workspace"
                  userName={undefined}
                  messages={messages}
                  threads={threads}
                  activeThreadId={activeThreadId}
                  activeSourceRef={null}
                  promptDraft={promptDraft}
                  promptWarning={promptWarning}
                  currentProductMode={currentProductMode}
                  isTablet={isTablet}
                  focusMode={expanded}
                  fullScreen={expanded}
                  showSidebar={false}
                  mainView={mainView}
                  sidebarView={sidebarView}
                  onSidebarViewChange={setSidebarView}
                  onBackHome={backHome}
                  onProductModeChange={setCurrentProductMode}
                  onNewChat={handleNewChat}
                  onStartTemporaryChat={handleStartTemporaryChat}
                  onStartChatWithPrompt={(prompt, selection) =>
                    void startChatWithPrompt(
                      prompt,
                      selection?.productMode || selection?.productIntent
                        ? {
                            productMode: selection.productMode ?? null,
                            productIntent: selection.productIntent ?? null,
                            modeSelectionSource: "manual",
                          }
                        : null,
                    )
                  }
                  onSwitchThread={handleSwitchThread}
                  onRenameThread={renameThread}
                  onDeleteThread={deleteThread}
                  onPinThread={pinThread}
                  onUnpinThread={unpinThread}
                  onArchiveThread={archiveThread}
                  onRestoreThread={restoreThread}
                  onStartSimilarChat={startSimilarChat}
                  onSetThreadMemoryMode={setThreadMemoryMode}
                  activeThreadMemoryMode={activeThread?.memoryMode ?? "default"}
                  artifactItems={artifacts}
                  artifactItemsLoading={artifactItemsLoading}
                  onOpenArtifact={(artifactId) => void handleOpenArtifact(artifactId)}
                  onDeleteArtifact={(artifactId) => void deleteArtifact(artifactId)}
                  onSetArtifactPinned={(artifactId, pinned) => void setArtifactPinned(artifactId, pinned)}
                  onPromptDraftChange={setPromptDraft}
                  onPromptSubmit={(prompt, selection) =>
                    void submitPrompt(
                      prompt,
                      selection?.productMode || selection?.productIntent
                        ? {
                            productMode: selection.productMode ?? null,
                            productIntent: selection.productIntent ?? null,
                            modeSelectionSource: "manual",
                          }
                        : null,
                    )
                  }
                  onEditMessage={editMessage}
                  onDeleteMessage={deleteMessage}
                  onMcqSubmit={submitMcq}
                  onEssayStart={startEssay}
                  onEssayDraftChange={changeEssayDraft}
                  onEssaySubmit={(blockId, prompt, draft) => void submitEssay(blockId, prompt, draft)}
                  onGuidedLessonSubmit={(input) => void submitGuidedLessonAnswer(input)}
                  onGuidedLessonContinue={(input) => void continueGuidedLesson(input)}
                  onGuidedLessonSkip={(input) => void skipGuidedLessonStep(input)}
                  onPlanSave={(messageId, blockId) => void savePlan(messageId, blockId)}
                  onPlanRefine={refinePlan}
                  onOpenTransparency={() => undefined}
                  homeSubtitle={surfaceContext.subtitle}
                  homeResumeTarget={resumeTarget}
                  onResumeTarget={handleResumeTarget}
                />
              </div>
            </div>
          </div>
        </ToofiShellSurface>
      )}
    </>
  );
}
